import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useSubscription = () => {
  const { 
    user, 
    session, 
    subscribed, 
    subscriptionTier, 
    subscriptionEnd, 
    checkSubscription 
  } = useAuth();
  const { toast } = useToast();

  const createCheckout = useCallback(async (plan: 'basic' | 'premium' | 'enterprise') => {
    if (!user || !session) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to subscribe.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Checkout error:', error);
        toast({
          title: 'Checkout failed',
          description: 'Failed to create checkout session. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      if (data?.url) {
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast({
        title: 'Checkout failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  }, [user, session, toast]);

  const manageSubscription = useCallback(async () => {
    if (!user || !session) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to manage your subscription.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Customer portal error:', error);
        toast({
          title: 'Portal access failed',
          description: 'Failed to access customer portal. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      if (data?.url) {
        // Open Stripe customer portal in a new tab
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Customer portal failed:', error);
      toast({
        title: 'Portal access failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  }, [user, session, toast]);

  return {
    subscribed,
    subscription_tier: subscriptionTier,
    subscription_end: subscriptionEnd,
    loading: false,
    refreshing: false,
    checkSubscription,
    createCheckout,
    manageSubscription,
  };
};